import { useState, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import api from '../utils/api';

/**
 * Custom hook สำหรับจัดการข้อมูล Projects
 * รองรับ caching, filtering, pagination และ real-time updates
 */
export const useProjects = (options = {}) => {
  const {
    page = 1,
    limit = 20,
    search = '', // ค้นหาจากชื่อหรือรหัสโครงการ
    status = '', // กรองตามสถานะ (planning, active, on_hold, completed, cancelled)
    category = '', // กรองตามหมวดหมู่
    priority = '', // กรองตามความสำคัญ (low, medium, high, urgent)
    manager_id = '', // กรองตามผู้จัดการโครงการ
    client_id = '', // กรองตามลูกค้า
    active = 'true', // กรองเฉพาะที่ใช้งาน
    sort_by = 'created_at', // เรียงตาม
    sort_order = 'DESC', // ASC หรือ DESC
    autoFetch = true, // เรียก API อัตโนมัติ
    enableCache = false // เปิดใช้งาน cache
  } = options;

  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [lastCacheKey, setLastCacheKey] = useState('');

  // Memoized cache keys สำหรับ localStorage
  const cacheKey = useMemo(() =>
    `projects_cache_${page}_${limit}_${search}_${status}_${category}_${priority}_${manager_id}_${client_id}_${active}_${sort_by}_${sort_order}`,
    [page, limit, search, status, category, priority, manager_id, client_id, active, sort_by, sort_order]
  );
  const cacheTimeKey = useMemo(() => `${cacheKey}_time`, [cacheKey]);
  const CACHE_DURATION = 2 * 60 * 1000; // 2 นาที (ข้อมูลโครงการเปลี่ยนแปลงบ่อยกว่าหน่วยนับ)

  /**
   * ตรวจสอบว่า cache ยังใช้ได้หรือไม่
   */
  const isCacheValid = useCallback(() => {
    if (!enableCache) return false;

    const cacheTime = localStorage.getItem(cacheTimeKey);
    if (!cacheTime) return false;

    const timeDiff = Date.now() - parseInt(cacheTime);
    return timeDiff < CACHE_DURATION;
  }, [cacheTimeKey, enableCache]);

  /**
   * โหลดข้อมูลจาก cache
   */
  const loadFromCache = useCallback(() => {
    if (!enableCache) return null;

    try {
      const cachedData = localStorage.getItem(cacheKey);
      return cachedData ? JSON.parse(cachedData) : null;
    } catch (error) {
      console.warn('Failed to load projects from cache:', error);
      return null;
    }
  }, [cacheKey, enableCache]);

  /**
   * บันทึกข้อมูลลง cache
   */
  const saveToCache = useCallback((data) => {
    if (!enableCache) return;

    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(cacheTimeKey, Date.now().toString());
      setLastCacheKey(cacheKey);
    } catch (error) {
      console.warn('Failed to save projects to cache:', error);
    }
  }, [cacheKey, cacheTimeKey, enableCache]);

  /**
   * ดึงข้อมูล projects จาก API
   */
  const fetchProjects = useCallback(async (forceRefresh = false) => {
    // ถ้าไม่ force refresh ให้ลองโหลดจาก cache ก่อน
    if (!forceRefresh && enableCache && isCacheValid()) {
      const cachedData = loadFromCache();
      if (cachedData) {
        setProjects(cachedData.data || []);
        setPagination(cachedData.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          totalPages: 0
        });
        setLastFetch(new Date());
        return cachedData;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit,
        search,
        status,
        category,
        priority,
        manager_id,
        client_id,
        active,
        sort_by,
        sort_order
      };

      // ลบค่า null หรือ empty string ออก
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await api.get('/api/projects', { params });

      if (response.data.success) {
        const projectsData = response.data.data;
        const paginationData = response.data.pagination;

        setProjects(projectsData);
        setPagination(paginationData);
        setLastFetch(new Date());
        saveToCache(response.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch projects');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลโครงการ';
      setError(errorMessage);
      //message.error(errorMessage);
      return { data: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, category, priority, manager_id, client_id, active, sort_by, sort_order, isCacheValid, loadFromCache, saveToCache]);

  /**
   * รีเฟรชข้อมูล (force reload)
   */
  const refreshProjects = useCallback(() => {
    return fetchProjects(true);
  }, [fetchProjects]);

  /**
   * ล้าง cache
   */
  const clearCache = useCallback(() => {
    if (enableCache) {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);
    }
  }, [cacheKey, cacheTimeKey, enableCache]);

  /**
   * ดึงข้อมูล project ตาม ID
   */
  const getProjectById = useCallback(async (id) => {
    try {
      const response = await api.get(`/api/projects/${id}`);

      if (response.data.success) {
        return response.data.data.project;
      } else {
        throw new Error(response.data.message || 'Project not found');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลโครงการ';
      message.error(errorMessage);
      return null;
    }
  }, []);

  /**
   * สร้างโครงการใหม่
   */
  const createProject = useCallback(async (projectData) => {
    try {
      const response = await api.post('/api/projects', projectData);

      if (response.data.success) {
        message.success('สร้างโครงการใหม่สำเร็จ');
        // รีเฟรชรายการเพื่อแสดงข้อมูลใหม่
        refreshProjects();
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create project');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการสร้างโครงการ';
      message.error(errorMessage);
      throw err;
    }
  }, [refreshProjects]);

  /**
   * อัปเดตโครงการ
   */
  const updateProject = useCallback(async (id, projectData) => {
    try {
      const response = await api.put(`/api/projects/${id}`, projectData);

      if (response.data.success) {
        message.success('อัปเดตโครงการสำเร็จ');
        // อัปเดตข้อมูลใน state ทันที
        setProjects(prev => prev.map(p => p.id === id ? response.data.data : p));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update project');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการอัปเดตโครงการ';
      message.error(errorMessage);
      throw err;
    }
  }, []);

  /**
   * ลบโครงการ
   */
  const deleteProject = useCallback(async (id) => {
    try {
      const response = await api.delete(`/api/projects/${id}`);

      if (response.data.success) {
        message.success(response.data.message || 'ลบโครงการสำเร็จ');
        // ลบข้อมูลออกจาก state ทันที
        setProjects(prev => prev.filter(p => p.id !== id));
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete project');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการลบโครงการ';
      message.error(errorMessage);
      throw err;
    }
  }, []);

  // Auto fetch เมื่อ mount หรือ dependencies เปลี่ยน
  // useEffect(() => {
  //   if (autoFetch) {
  //     fetchProjects();
  //   }
  // }, [autoFetch]);

  // ใหม่ (ทำงานแล้ว):
  useEffect(() => {
    if (autoFetch) {
      fetchProjects();
    }
  }, [autoFetch, page, limit, search, status, category, priority, manager_id, client_id, active, sort_by, sort_order, fetchProjects]);

  /**
   * ดึงสถิติโครงการ
   */
  const getDashboardStats = useCallback(async (filterOptions = {}) => {
    try {
      const params = { ...filterOptions };
      const response = await api.get('/api/projects/dashboard', { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch dashboard stats');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดสถิติโครงการ';
      message.error(errorMessage);
      return null;
    }
  }, []);

  /**
   * Memoized value สำหรับการนับจำนวนโครงการตามเงื่อนไขต่างๆ
   */
  const projectCounts = useMemo(() => {
    const counts = {
      total: projects.length,
      active: 0,
      planning: 0,
      on_hold: 0,
      completed: 0,
      cancelled: 0,
      urgent: 0,
      high_priority: 0,
      overdue: 0
    };

    const today = new Date().toISOString().split('T')[0];

    projects.forEach(project => {
      counts[project.status] = (counts[project.status] || 0) + 1;

      if (project.priority === 'urgent') {
        counts.urgent++;
      }
      if (project.priority === 'high') {
        counts.high_priority++;
      }

      if (project.end_date && project.end_date < today && project.status !== 'completed') {
        counts.overdue++;
      }
    });

    return counts;
  }, [projects]);


  /**
   * นับจำนวนโครงการตามเงื่อนไขต่างๆ
   */
  const getProjectCounts = useCallback(() => {
    return projectCounts;
  }, [projectCounts]);

  return {
    // ข้อมูลหลัก
    projects,
    pagination,
    loading,
    error,
    lastFetch,

    // ฟังก์ชันหลัก
    fetchProjects,
    refreshProjects,
    clearCache,
    isCacheValid: isCacheValid(),

    // ฟังก์ชันจัดการข้อมูล
    getProjectById,
    createProject,
    updateProject,
    deleteProject,
    getDashboardStats,

    // สถิติ
    projectCounts,

    // ตัวกรองปัจจุบัน (สำหรับ UI)
    currentFilters: {
      page, limit, search, status, category, priority,
      manager_id, client_id, active, sort_by, sort_order
    }
  };
};

/**
 * Custom hook สำหรับ Client Options ที่เหมาะกับ Ant Design Select
 * ดึงข้อมูลลูกค้าจาก contacts table
 */
export const useClientsOptions = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Fetching clients from: /api/contacts?type=customer&limit=1000');

      const response = await api.get('/api/contacts', {
        params: {
          type: 'customer', // ดึง contacts ที่มี type = 'customer' หรือ 'both'
          limit: 1000 // ดึงข้อมูลเยอะสำหรับ dropdown
        }
      });

      console.log('📡 Clients API Response:', response.data);

      if (response.data.success) {
        const clientsData = response.data.data;
        console.log('✅ Clients fetched successfully:', clientsData.length, 'clients');
        console.log('📋 Sample clients:', clientsData.slice(0, 2));
        setClients(clientsData);
        return clientsData;
      } else {
        throw new Error(response.data.message || 'Failed to fetch clients');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลลูกค้า';
      console.error('❌ Error fetching clients:', {
        message: errorMessage,
        status: err.response?.status,
        url: err.config?.url,
        params: err.config?.params
      });
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  /**
   * แปลง clients เป็น options format สำหรับ Ant Design
   */
  const clientOptions = useMemo(() => {
    return clients.map(client => ({
      value: client.id,
      label: client.company_name || client.name,
      client: client, // เก็บข้อมูลเต็มไว้ใช้งาน
      name: client.name,
      companyName: client.company_name,
      email: client.email,
      phone: client.phone
    }));
  }, [clients]);

  /**
   * ค้นหา client ตาม value
   */
  const findClientByValue = useCallback((value) => {
    return clients.find(client => client.id === value) || null;
  }, [clients]);

  /**
   * ดึงชื่อลูกค้าตาม value
   */
  const getClientName = useCallback((value) => {
    const client = findClientByValue(value);
    return client ? (client.company_name || client.name) : '';
  }, [findClientByValue]);

  return {
    clientOptions,
    clients,
    loading,
    error,
    refreshClients: fetchClients,
    findClientByValue,
    getClientName,
    isEmpty: clientOptions.length === 0
  };
};

/**
 * Custom hook สำหรับ Manager Options (Users)
 * ดึงข้อมูลผู้จัดการจาก users table
 */
export const useManagersOptions = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {

      //console.log('🔄 Fetching managers from: /api/users?role=ADMIN&limit=1000');

      const response = await api.get('/api/users', {
        params: {
          role: 'SUPER_ADMIN', // ดึงเฉพาะ ADMIN หรือ SUPER_ADMIN เป็นผู้จัดการโครงการได้
          limit: 1000 // ดึงข้อมูลเยอะสำหรับ dropdown
        }
      });

      console.log('📡 Managers API Response:', response.data);

      if (response.data.success) {
        const managersData = response.data.data;
        console.log('✅ Managers fetched successfully:', managersData.length, 'managers');
        console.log('📋 Sample managers:', managersData.slice(0, 2));
        setManagers(managersData);
        return managersData;
      } else {
        throw new Error(response.data.message || 'Failed to fetch managers');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้จัดการ';
      console.error('❌ Error fetching managers:', {
        message: errorMessage,
        status: err.response?.status,
        url: err.config?.url,
        params: err.config?.params
      });
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  /**
   * แปลง managers เป็น options format สำหรับ Ant Design
   */
  const managerOptions = useMemo(() => {
    return managers.map(manager => ({
      value: manager.id,
      label: manager.name, // ใช้ 'name' แทน 'full_name'
      manager: manager, // เก็บข้อมูลเต็มไว้ใช้งาน
      name: manager.name, // ใช้ 'name' แทน 'full_name'
      username: manager.email, // ใช้ email แทน username
      email: manager.email,
      role: manager.role_name || manager.role_code
    }));
  }, [managers]);

  /**
   * ค้นหา manager ตาม value
   */
  const findManagerByValue = useCallback((value) => {
    return managers.find(manager => manager.id === value) || null;
  }, [managers]);

  /**
   * ดึงชื่อผู้จัดการตาม value
   */
  const getManagerName = useCallback((value) => {
    const manager = findManagerByValue(value);
    return manager ? manager.name : ''; // ใช้ manager.name แทน manager.full_name
  }, [findManagerByValue]);

  return {
    managerOptions,
    managers,
    loading,
    error,
    refreshManagers: fetchManagers,
    findManagerByValue,
    getManagerName,
    isEmpty: managerOptions.length === 0
  };
};

/**
 * Custom hook สำหรับ Category Options (project_categories)
 * ดึงข้อมูลหมวดหมู่โครงการจากตาราง project_categories
 */
export const useCategoriesOptions = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Fetching categories from: /api/projects/categories');

      const response = await api.get('/api/projects/categories');

      console.log('📡 Categories API Response:', response.data);

      if (response.data.success) {
        const categoriesData = response.data.data;
        console.log('✅ Categories fetched successfully:', categoriesData.length, 'categories');
        console.log('📋 Sample categories:', categoriesData.slice(0, 2));
        setCategories(categoriesData);
        return categoriesData;
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดหมวดหมู่โครงการ';
      console.error('❌ Error fetching categories:', {
        message: errorMessage,
        status: err.response?.status,
        url: err.config?.url
      });
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * แปลง categories เป็น options format สำหรับ Ant Design
   */
  const categoryOptions = useMemo(() => {
    return categories.map(category => ({
      value: category.id,
      label: category.category_name,
      category: category, // เก็บข้อมูลเต็มไว้ใช้งาน
      name: category.category_name,
      color: category.category_color,
      description: category.description,
      totalProjects: category.total_projects || 0,
      activeProjects: category.active_projects || 0,
      completedProjects: category.completed_projects || 0,
      inactiveProjects: category.inactive_projects || 0
    }));
  }, [categories]);

  /**
   * ค้นหา category ตาม value
   */
  const findCategoryByValue = useCallback((value) => {
    return categories.find(category => category.id === value) || null;
  }, [categories]);

  /**
   * ดึงชื่อหมวดหมู่ตาม value
   */
  const getCategoryName = useCallback((value) => {
    const category = findCategoryByValue(value);
    return category ? category.category_name : '';
  }, [findCategoryByValue]);

  /**
   * ดึงสีของหมวดหมู่ตาม value
   */
  const getCategoryColor = useCallback((value) => {
    const category = findCategoryByValue(value);
    return category ? category.category_color : null;
  }, [findCategoryByValue]);

  return {
    categoryOptions,
    categories,
    loading,
    error,
    refreshCategories: fetchCategories,
    findCategoryByValue,
    getCategoryName,
    getCategoryColor,
    isEmpty: categoryOptions.length === 0
  };
};

/**
 * Custom hook สำหรับ Projects Options ที่เหมาะกับ Ant Design Select
 * รองรับการแปลงเป็น format { value, label }
 */
export const useProjectsOptions = (options = {}) => {
  const {
    status = 'active',
    category = '',
    search = '',
    exclude_completed = 'false',
    includeCode = true,
    valueField = 'id',
    sortBy = 'project_name'
  } = options;

  const { projects, loading, error, refreshProjects } = useProjects({
    status,
    category,
    search,
    exclude_completed,
    limit: 100, // ขอข้อมูลเยอะสำหรับ dropdown
    autoFetch: true,
    enableCache: true
  });

  /**
   * แปลง projects เป็น options format สำหรับ Ant Design
   */
  const projectOptions = useMemo(() => {
    if (!projects.length) return [];

    let processedProjects = [...projects];

    // เรียงข้อมูล
    processedProjects.sort((a, b) => {
      switch (sortBy) {
        case 'project_code':
          return a.project_code.localeCompare(b.project_code);
        case 'status':
          return a.status.localeCompare(b.status) ||
            a.project_name.localeCompare(b.project_name);
        case 'progress_percentage':
          return b.progress_percentage - a.progress_percentage;
        case 'created_at':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'project_name':
        default:
          return a.project_name.localeCompare(b.project_name);
      }
    });

    // แปลงเป็น options format
    return processedProjects.map(project => {
      let label = project.project_name;

      // เพิ่มรหัสโครงการใน label หากต้องการ
      if (includeCode && project.project_code) {
        label = `${project.project_name} (${project.project_code})`;
      }

      return {
        value: project[valueField],
        label,
        project: project, // เก็บข้อมูลเต็มไว้ใช้งาน
        code: project.project_code,
        status: project.status,
        priority: project.priority,
        progress: project.progress_percentage,
        category: project.category_name,
        categoryColor: project.category_color,
        manager: project.manager_name,
        endDate: project.end_date
      };
    });
  }, [projects, includeCode, valueField, sortBy]);

  /**
   * ค้นหา project ตาม value
   */
  const findProjectByValue = useCallback((value) => {
    return projects.find(project => project[valueField] === value) || null;
  }, [projects, valueField]);

  /**
   * ค้นหา project ตาม ID
   */
  const findProjectById = useCallback((id) => {
    return projects.find(project => project.id === id) || null;
  }, [projects]);

  /**
   * ดึงชื่อโครงการตาม value
   */
  const getProjectName = useCallback((value) => {
    const project = findProjectByValue(value);
    return project ? project.project_name : '';
  }, [findProjectByValue]);

  /**
   * ดึงรหัสโครงการตาม value
   */
  const getProjectCode = useCallback((value) => {
    const project = findProjectByValue(value);
    return project ? project.project_code : '';
  }, [findProjectByValue]);

  /**
   * ดึงสถานะโครงการตาม value
   */
  const getProjectStatus = useCallback((value) => {
    const project = findProjectByValue(value);
    return project ? project.status : '';
  }, [findProjectByValue]);

  /**
   * ดึงความก้าวหน้าของโครงการตาม value
   */
  const getProjectProgress = useCallback((value) => {
    const project = findProjectByValue(value);
    return project ? project.progress_percentage : 0;
  }, [findProjectByValue]);

  return {
    projectOptions,
    projects,
    loading,
    error,
    refreshProjects,
    findProjectByValue,
    findProjectById,
    getProjectName,
    getProjectCode,
    getProjectStatus,
    getProjectProgress,
    isEmpty: projectOptions.length === 0
  };
};

/**
 * Custom hook สำหรับจัดการ Projects Categories
 */
export const useProjectsCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/projects/categories');

      if (response.data.success) {
        setCategories(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดหมวดหมู่โครงการ';
      setError(errorMessage);
      message.error(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * แปลง categories เป็น options สำหรับ Select
   */
  const categoryOptions = useMemo(() => {
    return categories.map(cat => ({
      value: cat.id,
      label: `${cat.category_name} (${cat.active_projects}/${cat.total_projects})`,
      color: cat.category_color,
      description: cat.description,
      totalProjects: cat.total_projects,
      activeProjects: cat.active_projects,
      completedProjects: cat.completed_projects,
      inactiveProjects: cat.inactive_projects
    }));
  }, [categories]);

  return {
    categories,
    categoryOptions,
    loading,
    error,
    refreshCategories: fetchCategories
  };
};

/**
 * Custom hook สำหรับดึงข้อมูล Dashboard Stats
 */
export const useProjectsDashboard = (filters = {}) => {
  const [dashboardData, setDashboardData] = useState({
    summary: {},
    recent_projects: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { getDashboardStats } = useProjects({ ...filters, autoFetch: false });

  // ✅ ทำให้ filters stable ด้วย useMemo
  const stableFilters = useMemo(() => filters, [JSON.stringify(filters)]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getDashboardStats(stableFilters);

      console.log('dashboard data', data);

      if (data) {
        setDashboardData(data);
        return data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดสถิติโครงการ';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getDashboardStats, stableFilters]); // ✅ เปลี่ยนเป็น stableFilters

  // ✅ ดึงครั้งเดียวเมื่อ mount หรือเมื่อ filters จริง ๆ เปลี่ยน
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    dashboardData,
    loading,
    error,
    refreshDashboard: fetchDashboard
  };
};


/**
 * แปลงชื่อสถานะเป็นภาษาไทย
 */
export const getStatusDisplayName = (status) => {
  const statusNames = {
    planning: 'วางแผน',
    active: 'กำลังดำเนินการ',
    on_hold: 'พักชั่วคราว',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก'
  };

  return statusNames[status] || status;
};

/**
 * แปลงระดับความสำคัญเป็นภาษาไทย
 */
export const getPriorityDisplayName = (priority) => {
  const priorityNames = {
    low: 'ต่ำ',
    medium: 'ปานกลาง',
    high: 'สูง',
    urgent: 'เร่งด่วน'
  };

  return priorityNames[priority] || priority;
};

/**
 * แปลงชื่อหมวดหมู่เป็นภาษาไทย
 */
export const getCategoryDisplayName = (category) => {
  const categoryNames = {
    'Web Development': 'พัฒนาเว็บไซต์',
    'Mobile Development': 'พัฒนาแอปมือถือ',
    'Data Analytics': 'วิเคราะห์ข้อมูล',
    'Consulting': 'ที่ปรึกษา',
    'Maintenance': 'บำรุงรักษา',
    'Training': 'อบรม',
    'Research': 'วิจัยและพัฒนา'
  };

  return categoryNames[category] || category;
};

/**
 * ตรวจสอบว่าโครงการใกล้กำหนดหรือไม่
 */
export const isProjectNearDeadline = (project, days = 30) => {
  if (!project.end_date || project.status === 'completed') return false;

  const endDate = new Date(project.end_date);
  const today = new Date();
  const timeDiff = endDate.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  return daysDiff <= days && daysDiff > 0;
};

/**
 * ตรวจสอบว่าโครงการเกินกำหนดหรือไม่
 */
export const isProjectOverdue = (project) => {
  if (!project.end_date || project.status === 'completed') return false;

  const endDate = new Date(project.end_date);
  const today = new Date();
  return endDate < today;
};

/**
 * Utility function สำหรับแปลง project เป็น text แสดงผล
 */
export const formatProjectDisplay = (project, includeCode = true) => {
  if (!project) return '';

  let text = project.project_name;
  if (includeCode && project.project_code) {
    text += ` (${project.project_code})`;
  }

  return text;
};

export default useProjects;